import { useOutlookStore, useSettingStore, useThemeStore } from "@shared/stores";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import HStack from "@components/layout/HStack";
import TitleSetting from "@components/ui/TitleSetting";
import Divider from "@components/ui/Divider";
import Switch from "@components/ui/Switch";
import Select from "@components/ui/Select";
import Button from "@components/ui/Button";
import Combobox from "@components/ui/Combobox";
import { useDebounce } from "@shared/hooks/useDebounce";
import Accordion from "@components/ui/Accordion";
import { settingLabels } from "@shared/constants/setting";
import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import { useGlobalContext } from "@shared/contexts/GlobalContext";
import { useGetOrganizerAssistant } from "@features/settings/services/useGetOrganizerAssistant";
import { OrganizerEntity } from "@features/settings/services/entities/organizerEntity";
import { mailFlowOptions } from "@shared/constants/appConstants";
import { OrganizerSettingEntity } from "../services/entities/organizerSettingEntity";
import { MagnifyingGlass, PersonLinkIcon } from "@/pages/taskpane/components/icons/IconCustom";

const AssistantOrganizerSetting = () => {
  const { t } = useTranslation();
  const { setIsLoadingOverlay } = useGlobalContext();
  const { isDarkMode } = useThemeStore();
  // const showToast = useToastStore((state) => state.showToast);
  const { hasAssistant, toggleHasAssistant } = useSettingStore();
  const { organizer } = useOutlookStore();
  // const queryClient = useQueryClient();

  const { mutate: toggleAssistantOrganizer } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        // queryClient.setQueryData([QUERY_KEY.GET_ORGANIZER_SETTING, organizer], (oldData: OrganizerSettingEntity) => {
        //   return {
        //     ...oldData,
        //     hasAssistant: data.hasAssistant,
        //     assistantEmail: data.assistantEmail,
        //     assistantType: data.assistantType,
        //   };
        // });
        toggleHasAssistant();
        // showToast(t("Apply Successfully"), true);
      },
      onError: () => {
        // showToast(t("Apply Failed"), false);
      },
      onSettled: () => {
        setIsLoadingOverlay(false);
      },
    },
  });

  const handleToggleAssistantOrganizer = useCallback(() => {
    if (!organizer) return;
    setIsLoadingOverlay(true);
    let payload = {
      organizerEmail: organizer,
      hasAssistant: !hasAssistant,
    } as any;
    if (hasAssistant) {
      payload.assistantEmail = null;
      payload.assistantType = null;
    }
    toggleAssistantOrganizer(payload);
  }, [organizer, hasAssistant, toggleAssistantOrganizer]);

  const header = (
    <HStack justifyContent="between" className="w-full">
      <TitleSetting icon={<PersonLinkIcon isDarkMode={isDarkMode} />} label={t(settingLabels.assistantOrganizer)} />
      <Switch isChecked={hasAssistant} onAction={handleToggleAssistantOrganizer} />
    </HStack>
  );

  return (
    <Accordion header={header} isOpen={hasAssistant} isUseAccordionIcon={false}>
      {hasAssistant ? <AssistantOrganizerContent /> : null}
    </Accordion>
  );
};

export default AssistantOrganizerSetting;

// CONTENT

const AssistantOrganizerContent = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  // const showToast = useToastStore((state) => state.showToast);
  const { organizer } = useOutlookStore();
  const { assistantEmail, assistantType, assistantName, updateAssistantInfo } = useSettingStore();
  // const queryClient = useQueryClient();

  const [email, setEmail] = useState(assistantEmail);
  const [name, setName] = useState(assistantName);
  const [type, setType] = useState(assistantType);
  const [textSearch, setTextSearch] = useState(assistantEmail);
  const [isFocused, setIsFocused] = useState(false);

  const textDebounced = useDebounce(textSearch, 500);

  const { data: organizerAssistant, isFetching } = useGetOrganizerAssistant({
    payload: {
      organizerEmail: organizer,
      searchText: textDebounced || "",
    },
    enabled: !!textDebounced.trim() && isFocused,
  });
  const organizerAssistantOptions = useMemo(() => {
    return (organizerAssistant || [])?.map((item: OrganizerEntity) => ({
      label: item.defaultAssistantName,
      value: item.defaultAssistantEmail,
    }));
  }, [organizerAssistant]);

  const translatedMailFlowOptions = useMemo(() => {
    return mailFlowOptions.map((option) => ({
      ...option,
      label: t(option.label),
    }));
  }, [t]);

  const handleSelectAssistantOrganizer = (email: string) => {
    setEmail(email);
    setTextSearch(email);
    setName(
      organizerAssistantOptions.find(
        (option: { value: string; label: string }) => option.value.toLowerCase() === email.toLowerCase()
      )?.label ?? ""
    );
  };

  const { mutate: updateAssistantInfoMutation, isPending: isUpdatingAssistantInfo } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        updateAssistantInfo(email, type, name);
        // queryClient.setQueryData([QUERY_KEY.GET_ORGANIZER_SETTING, organizer], (oldData: OrganizerSettingEntity) => {
        //   return {
        //     ...oldData,
        //     assistantEmail: data.assistantEmail,
        //     assistantType: data.assistantType,
        //   };
        // });
        // showToast(t("Apply Successfully"), true);
      },
      onError: () => {
        // showToast(t("Apply Failed"), false);
      },
    },
  });

  const handleApply = useCallback(() => {
    if (!email || !organizer) return;
    updateAssistantInfoMutation({
      organizerEmail: organizer,
      assistantEmail: email,
      assistantType: type,
      assistantName: name,
    });
  }, [email, type, organizer, updateAssistantInfoMutation, name]);

  return (
    <div className="w-full">
      <Divider className="my-3" />

      {/* Input Assistant Organizer */}
      <p className="text-sm font-semibold mb-[6px]">{t("Assistant Organizer")}</p>
      <Combobox
        placeholder={t("Enter a part of the name to search for")}
        className="w-full h-[32px] mb-3 text-sm"
        leftIcon={<MagnifyingGlass isDarkMode={isDarkMode} size={20} />}
        options={organizerAssistantOptions}
        onChangeInput={(value) => setTextSearch(value as string)}
        textSearch={textSearch}
        onSelectOption={(value) => handleSelectAssistantOrganizer(value as string)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        isLoading={isFetching}
      />

      {/* Select Mail Flow */}
      <p className="text-sm font-semibold mb-[6px]">{t("Mail Flow")}</p>
      <Select
        placeholder={t("Select Mail Flow")}
        className="w-full h-[32px] mb-3 text-sm"
        options={translatedMailFlowOptions}
        value={type}
        onChange={(value) => setType(value as number)}
      />

      <Divider className="my-3" />

      <HStack justifyContent="end" className="w-full">
        <Button
          isDefault={true}
          className="w-[50%] py-2 text-xs"
          label={t("Apply")}
          onClick={handleApply}
          disabled={!email || !(type === 0 || type) || email !== textSearch || isUpdatingAssistantInfo}
        />
      </HStack>
    </div>
  );
};
